/**
 * Platform Billing API - SaaS subscription billing (Stripe Billing).
 *
 * Note: these endpoints return raw JSON bodies (not ApiResponse-wrapped),
 * so we use the *Raw helpers.
 */

import { apiDelete, apiDeleteRaw, apiGet, apiGetRaw, apiPost, apiPostRaw, apiPutRaw } from "./client";
import type {
  PlatformInvoiceListResponse,
  PlatformLedgerInvoice,
  PlatformMeteredUsage,
  PlatformUpcomingInvoice,
  PlanWorkspace,
  PlatformPlan,
  PlatformSetupIntentResponse,
  PlatformSubscription,
  PlatformUsage,
} from "@/types";

export function listPlatformPlans(): Promise<PlatformPlan[]> {
  return apiGetRaw<PlatformPlan[]>("/platform/plans");
}

export function createPlatformSetupIntent(body?: {
  billing_email?: string;
}): Promise<PlatformSetupIntentResponse> {
  return apiPostRaw<PlatformSetupIntentResponse>(
    "/platform/billing/setup-intent",
    body ?? {},
  );
}

/** Menu mode for the signed-in clinic (PRP-only or full EHR). */
export function getPlanWorkspace(): Promise<PlanWorkspace> {
  return apiGetRaw<PlanWorkspace>("/platform/billing/workspace");
}

export async function getPlatformSubscription(): Promise<PlatformSubscription | null> {
  try {
    return await apiGetRaw<PlatformSubscription>(
      "/platform/billing/subscription",
    );
  } catch (err) {
    if ((err as { status?: number } | undefined)?.status === 404) return null;
    throw err;
  }
}

export function createPlatformSubscription(req: {
  plan_id: string;
  /** Stripe card only; Paystack checkouts send none. */
  payment_method_id?: string;
  billing_interval: "month" | "year";
  addon_keys?: string[];
  billing_email?: string;
}): Promise<PlatformSubscription> {
  return apiPostRaw<PlatformSubscription>("/platform/billing/subscribe", req);
}

/** Opens Paystack (MoMo or card) for an open invoice. autopay saves a card. */
export function payPlatformInvoice(invoiceId: string, autopay = false): Promise<{ authorization_url: string; reference: string }> {
  return apiPost(`/platform/billing/invoices/${invoiceId}/pay`, { autopay });
}

/** Records a Paystack payment when the practice comes back from Paystack. */
export function confirmPaystackPayment(reference: string): Promise<{ number?: string; status?: string }> {
  return apiPost("/platform/billing/paystack/confirm", { reference });
}

/** "I've paid, check now": asks Paystack for payments not yet applied. */
export function syncPaystackPayments(): Promise<{ credited: number; message: string }> {
  return apiPost("/platform/billing/paystack/sync", {});
}

/** Stops automatic card charges; future invoices get a pay link again. */
export function stopPaystackAutopay(): Promise<void> {
  return apiDelete("/platform/billing/paystack/card");
}

export function changePlatformPlan(req: {
  plan_id: string;
  billing_interval: "month" | "year";
  proration_behavior?: "create_prorations" | "none" | "always_invoice";
}): Promise<PlatformSubscription> {
  return apiPutRaw<PlatformSubscription>(
    "/platform/billing/subscription",
    req,
  );
}

export function cancelPlatformSubscription(): Promise<{ message: string }> {
  return apiDeleteRaw<{ message: string }>("/platform/billing/subscription");
}

export async function getPlatformUsage(): Promise<PlatformUsage | null> {
  try {
    return await apiGetRaw<PlatformUsage>("/platform/billing/usage");
  } catch (err) {
    if ((err as { status?: number } | undefined)?.status === 404) return null;
    throw err;
  }
}

export function listPlatformInvoices(params?: {
  page?: number;
  page_size?: number;
}): Promise<PlatformInvoiceListResponse> {
  return apiGetRaw<PlatformInvoiceListResponse>("/platform/billing/invoices", {
    params,
  });
}

export function createPlatformCustomerPortal(returnUrl?: string): Promise<{ url: string }> {
  return apiPostRaw<{ url: string }>("/platform/billing/portal", undefined, {
    params: returnUrl ? { return_url: returnUrl } : undefined,
  });
}

export function updatePlatformAddons(addonKeys: string[]): Promise<PlatformSubscription> {
  return apiPutRaw<PlatformSubscription>("/platform/billing/addons", {
    addon_keys: addonKeys,
  });
}

/** Current-month consumption meters (AI notes, eligibility checks). ApiResponse-wrapped. */
export async function getPlatformMeteredUsage(): Promise<PlatformMeteredUsage | null> {
  try {
    return await apiGet<PlatformMeteredUsage>("/platform/billing/metered-usage");
  } catch (err) {
    if ((err as { status?: number } | undefined)?.status === 404) return null;
    throw err;
  }
}

/** One in-house invoice with every line and payment. Raw JSON body. */
export function getPlatformLedgerInvoice(id: string): Promise<PlatformLedgerInvoice> {
  return apiGetRaw<PlatformLedgerInvoice>(`/platform/billing/invoices/${id}`);
}

/** What the next invoice will contain if nothing changes. Raw JSON body. */
export function getPlatformUpcomingInvoice(): Promise<PlatformUpcomingInvoice> {
  return apiGetRaw<PlatformUpcomingInvoice>("/platform/billing/upcoming");
}

/** Downloads one of this organization's invoices as a PDF. */
export async function downloadPlatformInvoicePdf(id: string, fileName: string): Promise<void> {
  const apiClient = (await import("./client")).default;
  const res = await apiClient.get<Blob>(`/platform/billing/invoices/${id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
